import React from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  Stack,
  CardActionArea,
  CardMedia,
  CardContent,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Chip,
  Container
} from "@mui/material";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Facebook,
  Twitter,
  Instagram,
  YouTube,
  LinkedIn,
  Email,
  Phone,
  LocationOn,
} from "@mui/icons-material";
import FallbackContent from "../components/FallbackContent";

const PoliticsNewsPage = () => {
  const [politicsStories, setPoliticsStories] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  
  const categories = [
    "All",
    "Politics",
    "Business",
    "Economy",
    "International",
    "Technology",
  ];

  React.useEffect(() => {
    const fetchPoliticsStories = async () => {
      try {
        const response = await fetch("https://ethioinsider-backend.onrender.com/api/articles/politics");
        if (!response.ok) {
          throw new Error("Failed to fetch politics stories");
        }
        const result = await response.json();
        if (result.data && Array.isArray(result.data)) {
          setPoliticsStories(result.data);
        } else {
          throw new Error("Unexpected API response format");
        }
      } catch (err) {
        setError(err.message);
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchPoliticsStories();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4, mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography
        variant="body1"
        color="error"
        sx={{ textAlign: "center", py: 4, mt: 10 }}
      >
        Error loading politics stories: {error}
      </Typography>
    );
  }

  if (politicsStories.length === 0) {
    return <FallbackContent category="politics" />;
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
        Politics News
      </Typography>

      {/* News Grid with Fixed-Size Boxes */}
      <Grid container spacing={3}>
        {politicsStories.map((story) => (
                        <Grid xs={12} sm={6} md={4} key={`politics-${story._id}`}>
            <Box sx={{ 
              height: "100%",
              display: "flex",
              flexDirection: "column"
            }}>
              <Card square elevation={1} sx={{ 
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.3s, box-shadow 0.3s",
                "&:hover": {
                  transform: "translateY(-5px)",
                  boxShadow: 3
                }
              }}>
                <CardActionArea 
                  component={Link} 
                  to={`/allpolnews/${story._id}`}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    flexGrow: 1
                  }}
                >
                  {/* Image Section - Fixed Height */}
                  <Box sx={{ 
                    width: "100%",
                    height: "200px",
                    overflow: "hidden",
                    position: "relative"
                  }}>
                    <CardMedia
                      component="img"
                      image={
                        story?.imageUrl ||
                        "https://via.placeholder.com/400x250?text=Politics"
                      }
                      alt={story?.title}
                      sx={{ 
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        transition: "transform 0.3s",
                      }}
                      onError={(e) => {
                        e.target.src = "https://via.placeholder.com/400x250?text=Politics";
                      }}
                    />
                    {story.isBreaking && (
                      <Chip
                        label="BREAKING"
                        color="error"
                        size="small"
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          fontWeight: "bold",
                        }}
                      />
                    )}
                  </Box>

                  {/* Content Section - Flexible Height */}
                  <CardContent sx={{ 
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    p: 2
                  }}>
                    <Typography
                      variant="subtitle1"
                      gutterBottom
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "3em",
                        fontWeight: "bold",
                        mb: 1,
                        width:"400px"
                      }}
                    >
                      {story?.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        display: "-webkit-box",
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        flexGrow: 1,
                        mb: 1.5,
                        width:"400px"
                      }}
                    >
                      {story?.excerpt || "No excerpt available"}
                    </Typography>
                    <Box sx={{ 
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                      >
                        {new Date(story?.createdAt).toLocaleDateString()}
                      </Typography>
                      <Button 
                        variant="text" 
                        size="small" 
                        sx={{ 
                          textTransform: "none",
                          fontWeight: "bold"
                        }}
                      >
                        Read More
                      </Button>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Box>
          </Grid>
        ))}
      </Grid>

       {/* Footer */}
            <Box
              component="footer"
              sx={{
                py: 4,
                px: 2,
                backgroundColor: "background.paper",
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Container maxWidth="lg">
                          <Grid container spacing={4}>
            {/* Quick Links */}
            <Grid xs={12} sm={6} md={3}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                      Quick Links
                    </Typography>
                                     <Stack spacing={1}>
                   {[
                     { name: "Home", path: "/" },
                     { name: "About Us", path: "/about" },
                     { name: "Contact", path: "/contact" }
                   ].map((item) => (
                     <motion.div whileHover={{ x: 5 }} key={item.name}>
                       <Link
                         to={item.path}
                         style={{
                           display: "block",
                           color: "inherit",
                           textDecoration: "none",
                         }}
                         sx={{
                           color: "text.secondary",
                           "&:hover": { color: "primary.main" },
                         }}
                       >
                         {item.name}
                       </Link>
                     </motion.div>
                   ))}
                    </Stack>
                  </Grid>
      
                  {/* Categories */}
                  <Grid xs={12} sm={6} md={3}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                      Categories
                    </Typography>
                    <Stack spacing={1}>
                      {["Politics", "Business", "Sports"].map((category) => (
                        <motion.div whileHover={{ x: 5 }} key={category}>
                          <Link
                            to={`/${category.toLowerCase()}-news`}
                            style={{
                              display: "block",
                              color: "inherit",
                              textDecoration: "none",
                            }}
                            sx={{
                              color: "text.secondary",
                              "&:hover": { color: "primary.main" },
                            }}
                          >
                            {category}
                          </Link>
                        </motion.div>
                      ))}
                    </Stack>
                  </Grid>
      
                  {/* Contact Info */}
                  <Grid xs={12} sm={6} md={3}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                      Contact Us
                    </Typography>
                    <Stack spacing={1.5}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <LocationOn color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2">
                          123 News Street, Addis Ababa, Ethiopia
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Phone color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2">+251 123 456 789</Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Email color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2">info@ethioreview.com</Typography>
                      </Box>
                    </Stack>
                  </Grid>
      
                  {/* Social Media & Newsletter */}
                  <Grid xs={12} sm={6} md={3}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                      Follow Us
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                      {[
                        { icon: <Facebook />, color: "#3b5998", name: "Facebook" },
                        { icon: <Twitter />, color: "#1DA1F2", name: "Twitter" },
                        { icon: <Instagram />, color: "#E1306C", name: "Instagram" },
                        { icon: <YouTube />, color: "#FF0000", name: "YouTube" },
                        { icon: <LinkedIn />, color: "#0077B5", name: "LinkedIn" },
                      ].map((social) => (
                        <motion.div whileHover={{ scale: 1.1 }} key={social.name}>
                          <IconButton
                            href="#"
                            target="_blank"
                            rel="noopener"
                            sx={{
                              backgroundColor: `${social.color}10`,
                              color: social.color,
                              "&:hover": { backgroundColor: `${social.color}20` },
                            }}
                          >
                            {social.icon}
                          </IconButton>
                        </motion.div>
                      ))}
                    </Stack>
      
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
                      Newsletter
                    </Typography>
                    <Box component="form" sx={{ display: "flex" }}>
                      <TextField
                        size="small"
                        placeholder="Your email"
                        variant="outlined"
                        fullWidth
                        sx={{ mr: 1 }}
                      />
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Subscribe
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
      
                {/* Copyright & Legal */}
                <Box
                  sx={{
                    mt: 4,
                    pt: 3,
                    borderTop: 1,
                    borderColor: "divider",
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    © {new Date().getFullYear()} EthioReview. All rights reserved.
                  </Typography>
      
                  <Stack direction="row" spacing={2} sx={{ mt: { xs: 2, sm: 0 } }}>
                    {[
                      { name: "Privacy Policy", path: "/privacy" },
                      { name: "Terms of Service", path: "/terms" },
                      { name: "Cookie Policy", path: "/cookies" }
                    ].map((item) => (
                      <Typography
                        key={item.name}
                        component={Link}
                        to={item.path}
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          textDecoration: "none",
                          "&:hover": { color: "primary.main" },
                        }}
                      >
                        {item.name}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              </Container>
            </Box>
      
    </Box>
  );
};

export default PoliticsNewsPage;